<div class="relative flex items-center gap-2 rounded-2xl bg-[#ECEEF3] p-2 sm:p-3">
  @if (hasOverflow) {
    <button
      type="button"
      [disabled]="!canScrollLeft"
      (click)="scrollByStep(-1)"
      [attr.aria-label]="'DOC_BUILDER.TABS.SCROLL_LEFT' | translate"
      class="hidden h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-[#1E2A78] shadow-sm transition hover:bg-indigo-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white sm:flex"
    >
      <span aria-hidden="true" class="inline-block rtl:-scale-x-100">‹</span>
    </button>
  }

  <div
    role="tablist"
    [attr.aria-label]="'DOC_BUILDER.TABS.SECTIONS' | translate"
    #scrollTrack
    (scroll)="onTrackScroll()"
    class="scrollbar-hide flex flex-1 gap-2 overflow-x-auto scroll-smooth"
  >
    @for (tab of tabs; track tab.id; let i = $index) {
      <button
        #tabButton
        type="button"
        role="tab"
        [id]="'tab-' + tab.id"
        [attr.aria-controls]="panelId"
        [attr.aria-selected]="activeIndex === i"
        [attr.tabindex]="activeIndex === i ? 0 : -1"
        [attr.aria-label]="(sectionTitleKey(tab.title) | translate) + (statusLabel(tab) ? ', ' + (statusLabel(tab) | translate) : '')"
        [title]="sectionTitleKey(tab.title) | translate"
        (click)="selectTab(i)"
        (keydown)="onTabKeydown($event, i)"
        class="group flex min-h-11 shrink-0 items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
        [ngClass]="{
          'bg-white text-[#1E2A78] shadow-md': activeIndex === i,
          'text-gray-500 hover:bg-white/60 hover:text-[#1E2A78]': activeIndex !== i
        }"
      >
        <span
          class="relative flex h-3.5 w-3.5 shrink-0 items-center justify-center"
          aria-hidden="true"
        >
          <ng-container *ngTemplateOutlet="statusIcon; context: { $implicit: statusFor(tab) }"></ng-container>
        </span>

        <span class="whitespace-nowrap">{{ sectionTitleKey(tab.title) | translate }}</span>

        <span class="sr-only">{{ statusLabel(tab) | translate }}</span>
      </button>
    }
  </div>

  @if (hasOverflow) {
    <button
      type="button"
      [disabled]="!canScrollRight"
      (click)="scrollByStep(1)"
      [attr.aria-label]="'DOC_BUILDER.TABS.SCROLL_RIGHT' | translate"
      class="hidden h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-[#1E2A78] shadow-sm transition hover:bg-indigo-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white sm:flex"
    >
      <span aria-hidden="true" class="inline-block rtl:-scale-x-100">›</span>
    </button>
  }
</div>

<div
  class="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-slate-100 px-4 py-2.5 text-[11px] font-medium text-slate-500"
  role="note"
  [attr.aria-label]="'DOC_BUILDER.TABS.LEGEND' | translate"
>
  <span class="font-semibold text-slate-600">{{ 'DOC_BUILDER.TABS.LEGEND' | translate }}:</span>

  @for (item of legendItems; track item.status) {
    <span class="inline-flex items-center gap-1.5">
      <span class="flex h-3.5 w-3.5 shrink-0 items-center justify-center" aria-hidden="true">
        <ng-container *ngTemplateOutlet="statusIcon; context: { $implicit: item.status }"></ng-container>
      </span>
      {{ item.label | translate }}
    </span>
  }
</div>

<!-- Single source for the status icon, shared by the tabs and the legend. -->
<ng-template #statusIcon let-status>
  @switch (status) {
    @case ('saving') {
      <svg
        class="h-3.5 w-3.5 animate-spin text-indigo-600"
        viewBox="0 0 24 24"
        fill="none"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        ></circle>
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
        ></path>
      </svg>
    }

    @case ('saved') {
      <svg
        class="h-3.5 w-3.5 text-emerald-600"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fill-rule="evenodd"
          d="M16.7 5.3a1 1 0 010 1.4l-7 7a1 1 0 01-1.4 0l-3-3a1 1 0 111.4-1.4L9 11.6l6.3-6.3a1 1 0 011.4 0z"
          clip-rule="evenodd"
        />
      </svg>
    }

    @case ('validation-error') {
      <svg
        class="h-3.5 w-3.5 text-red-600"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fill-rule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v4a1 1 0 102 0V7zm-1 7a1 1 0 100 2h.01a1 1 0 100-2H10z"
          clip-rule="evenodd"
        />
      </svg>
    }

    @case ('api-error') {
      <svg
        class="h-3.5 w-3.5 text-red-600"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fill-rule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v4a1 1 0 102 0V7zm-1 7a1 1 0 100 2h.01a1 1 0 100-2H10z"
          clip-rule="evenodd"
        />
      </svg>
    }

    @case ('dirty') {
      <span class="h-2 w-2 rounded-full bg-amber-500"></span>
    }

    @default {
      <span class="h-1.5 w-1.5 rounded-full bg-slate-300"></span>
    }
  }
</ng-template>
